import { useEffect, useState } from 'react';
import {
  useParams,
  Navigate,
  useSearchParams,
  useBlocker,
} from 'react-router-dom';
import {
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from '../lib/api';
import { useCurrentCalendar } from '../contexts/CurrentCalendarContext';
import { useBreakpoint } from '../hooks/useBreakpoint';
import {
  CalendarPageProvider,
  useCalendarPage,
} from '../contexts/CalendarPageContext';
import { useAllCalendars } from '../contexts/AllCalendarsContext';
import { DAYS, MONTHS } from '../lib/dateUtils';
import EventPanel from '../components/EventPanel';
import TrialBanner from '../components/TrialBanner';
import DayEventsModal from '../components/DayEventsModal';
import GuestSubscribeModal from '../components/GuestSubscribeModal';
import CalendarGrid from '../components/CalendarGrid';
import EventsStatusIndicator from '../components/EventsStatusIndicator';

export default function CalendarRouter() {
  const queryClient = useQueryClient();
  const { calendarId } = useParams<{
    calendarId: string;
  }>();
  const [searchParams] = useSearchParams();
  const joinToken = searchParams.get('join_token');
  const { setGuestCalendar } = useAllCalendars();

  const {
    data: meData,
    isError: meError,
    refetch: refetchMe,
  } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me.get(),
  });

  const personalCalendarId =
    meData?.user?.personal_calendar_id;

  // Fetch calendar with join token if present (guest access)
  const {
    data: calendarData,
    isLoading: isCalendarLoading,
    isError: isCalendarError,
  } = useQuery({
    queryKey: ['calendar-access', calendarId, joinToken],
    queryFn: () =>
      api.calendars.get(
        calendarId!,
        joinToken ? { join_token: joinToken } : undefined
      ),
    enabled: Boolean(calendarId && joinToken),
    retry: false,
  });

  const fetchedCalendar = calendarData?.calendar as
    | Record<string, unknown>
    | undefined;
  const accessVia = (fetchedCalendar as any)?.access_via as
    | 'membership'
    | 'join_token'
    | null
    | undefined;
  const isMember = (fetchedCalendar as any)?.is_member as
    | boolean
    | undefined;
  const isGuestAccess =
    accessVia === 'join_token' && !isMember;

  // Debug logging for access state
  useEffect(() => {
    if (calendarData) {
      console.log('[CalendarRouter] Access state:', {
        accessVia,
        isMember,
        isGuestAccess,
        isCalendarLoading,
        isCalendarError,
      });
    }
  }, [
    calendarData,
    accessVia,
    isMember,
    isGuestAccess,
    isCalendarLoading,
    isCalendarError,
  ]);

  // Bubble guest calendar up to AllCalendarsContext for sidebar
  useEffect(() => {
    if (isGuestAccess && fetchedCalendar) {
      setGuestCalendar({
        ...fetchedCalendar,
        role: 'guest',
      } as any);
      return () => {
        setGuestCalendar(null);
      };
    }
  }, [isGuestAccess, fetchedCalendar, setGuestCalendar]);

  useEffect(() => {
    if (!calendarId && personalCalendarId) {
      queryClient.invalidateQueries({
        queryKey: ['calendars'],
      });
    }
  }, [calendarId, personalCalendarId, queryClient]);

  if (!calendarId) {
    if (personalCalendarId) {
      return (
        <Navigate
          to={`/calendar/${personalCalendarId}`}
          replace
        />
      );
    }
    if (meError) {
      return (
        <div className="flex flex-col items-center justify-center h-full min-h-64 gap-3">
          <span className="font-serif text-xl font-bold text-ink-900">
            CommonCal
          </span>
          <span className="font-mono text-ink-400 text-sm">
            Could not load your calendar.
          </span>
          <button
            onClick={() => void refetchMe()}
            className="font-mono text-xs text-ink-400 underline hover:text-ink-700 transition-colors"
          >
            Try again
          </button>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-64 gap-4">
        <span className="font-serif text-xl font-bold text-ink-900">
          CommonCal
        </span>
        <span className="block w-2 h-2 rounded-full bg-ink-200 animate-pulse" />
      </div>
    );
  }

  // If join token provided but failed to load → 404
  if (joinToken && isCalendarError) {
    return (
      <div className="flex items-center justify-center h-full min-h-64">
        <span className="font-mono text-ink-400 text-sm">
          Calendar not found.
        </span>
      </div>
    );
  }

  // If join token provided but still loading
  if (joinToken && isCalendarLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-64 gap-4">
        <span className="font-serif text-xl font-bold text-ink-900">
          CommonCal
        </span>
        <span className="block w-2 h-2 rounded-full bg-ink-200 animate-pulse" />
      </div>
    );
  }

  return (
    <CalendarPageProvider
      calendarId={calendarId}
      joinToken={joinToken}
      isMember={isMember}
      accessVia={accessVia ?? null}
    >
      <CalendarPage />
    </CalendarPageProvider>
  );
}

function CalendarPage() {
  const isDesktop = useBreakpoint('sm');
  const { theme } = useCurrentCalendar();

  const {
    calendarId,
    currentMonth,
    prevMonth,
    nextMonth,
    goToToday,
    panelOpen,
    closePanel,
    editEventId,
    editOccurrenceDate,
    createForDate,
    expandedDay,
    closeDayModal,
    handleModalEventClick,
    handleModalCreateEvent,
    expandedEvents,
    eventsOnDay,
    multiDayOnDay,
    calendarColorMap,
    calColor,
    currentCalendar,
    showTrialBanner,
    trialBannerDismissed: _trialBannerDismissed,
    setTrialBannerDismissed,
    billingData,
    showSubscribeButton,
    isSubscribed,
    createSubscription,
    navigate,
    isTemporaryVisitor,
    joinToken,
    holidaysByDay,
  } = useCalendarPage();

  // Get meData for subscription functionality
  const { data: meData } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me.get(),
  });

  const [showTypeTip, setShowTypeTip] = useState(false);
  const [guestModalDismissed, setGuestModalDismissed] =
    useState(false);

  const showGuestModal =
    isTemporaryVisitor &&
    Boolean(showSubscribeButton) &&
    !isSubscribed &&
    !guestModalDismissed &&
    !createSubscription.isSuccess;

  const calendarType =
    currentCalendar && !currentCalendar.is_personal
      ? currentCalendar.is_kocal
        ? 'kocal'
        : 'bulletin'
      : null;

  const calendarTypeDescription =
    calendarType === 'kocal'
      ? 'Members can contribute events from their own calendars.'
      : 'Subscribe to see events posted by the owner.';

  // Block in-app navigation for temporary visitors and show a custom confirm dialog
  // Skip the warning if user has already subscribed to this calendar
  useBlocker(({ currentLocation, nextLocation }) => {
    if (!isTemporaryVisitor || isSubscribed) return false;
    if (currentLocation.pathname === nextLocation.pathname)
      return false;
    return !window.confirm(
      "You're viewing this calendar as a guest. If you leave, you'll need the original link to get back.\n\nLeave anyway?"
    );
  });

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Left: Calendar area */}
      <div className="flex-1 min-w-0 overflow-y-auto overflow-x-auto p-6">
        {showTrialBanner && (
          <div className="mb-4">
            <TrialBanner
              trialEndsAt={
                billingData!.plan.trial_ends_at ?? null
              }
              onDismiss={() =>
                setTrialBannerDismissed(true)
              }
            />
          </div>
        )}

        {/* Calendar identity + month nav */}
        <div className="mb-6">
          {currentCalendar && (
            <div className="flex items-center gap-2.5 mb-3">
              <span
                className="w-3.5 h-3.5 rounded-sm shrink-0"
                style={{ backgroundColor: calColor }}
              />
              <div>
                <span className="font-serif text-xl font-bold text-ink-800">
                  {currentCalendar.name}
                </span>
                {/* Show guest indicator underneath calendar name */}
                {isTemporaryVisitor && (
                  <div className="font-mono text-xs text-ink-400 mt-1">
                    guest
                  </div>
                )}
                {/* Calendar type label (kocal / bulletin) */}
                {calendarType && (
                  <div className="relative mt-1">
                    <button
                      type="button"
                      onClick={() =>
                        setShowTypeTip(v => !v)
                      }
                      className="flex items-center gap-1 font-mono text-xs text-ink-400 border-b border-dotted border-ink-300 hover:text-ink-600 hover:border-ink-500 transition-colors leading-none pb-px"
                    >
                      {calendarType}
                      <svg
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        className="w-3 h-3 shrink-0"
                      >
                        <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0Zm0 12a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm1-3.5H7V4h2v4.5Z" />
                      </svg>
                    </button>
                    {showTypeTip && (
                      <div className="absolute left-0 top-full mt-2 w-56 bg-ink-900 text-paper-50 font-mono text-xs rounded-sm px-3 py-2 z-50 shadow-lg">
                        {calendarTypeDescription}
                        <button
                          type="button"
                          onClick={() =>
                            setShowTypeTip(false)
                          }
                          className="absolute top-1 right-2 text-paper-50/60 hover:text-paper-50"
                          aria-label="Close"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={goToToday}
                  className="px-2 py-1 text-xs font-mono text-ink-500 hover:text-ink-700 hover:bg-ink-100 rounded transition-colors"
                >
                  today
                </button>
                {showSubscribeButton && (
                  <button
                    onClick={() => {
                      if (isSubscribed) {
                        navigate(
                          `/settings?calendar=${calendarId}`
                        );
                      } else {
                        const token =
                          joinToken ??
                          currentCalendar?.join_token;
                        if (
                          token &&
                          meData?.user?.personal_calendar_id
                        ) {
                          createSubscription.mutate({
                            join_token: token,
                            subscriber_calendar_id:
                              meData.user
                                .personal_calendar_id,
                          });
                        }
                      }
                    }}
                    disabled={createSubscription.isPending}
                    className="px-3 py-1.5 text-xs font-mono text-white rounded-sm hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: calColor }}
                  >
                    {isSubscribed ? 'Manage' : 'Subscribe'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Timezone pillbox - clickable to settings */}
          {currentCalendar?.timezone && (
            <div className="flex justify-center mb-3">
              <button
                onClick={() =>
                  navigate(
                    `/settings?calendar=${calendarId}#timezone`
                  )
                }
                className="group inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono transition-opacity hover:opacity-90"
                style={{
                  backgroundColor: calColor,
                  color: '#ffffff',
                }}
                title="Click to change timezone in settings"
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="max-w-[200px] truncate">
                  {currentCalendar.timezone}
                </span>
                {/* Disclosure arrow - appears on hover */}
                <svg
                  className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          )}

          <div
            className="grid items-center mb-1"
            style={{ gridTemplateColumns: '52px 1fr 52px' }}
          >
            <button
              onClick={prevMonth}
              aria-label="Previous month"
              className="w-12 h-12 flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 transition-colors font-mono text-2xl leading-none"
              onMouseEnter={e =>
                (e.currentTarget.style.backgroundColor =
                  theme.surface3)
              }
              onMouseLeave={e =>
                (e.currentTarget.style.backgroundColor = '')
              }
            >
              ‹
            </button>
            <div className="text-center">
              <h2
                className="font-serif text-3xl font-bold text-ink-900 inline-block border-b-2"
                style={{ borderBottomColor: calColor }}
              >
                {MONTHS[currentMonth.getMonth()]}
              </h2>
              <div className="font-mono text-base text-ink-400 mt-0.5">
                {currentMonth.getFullYear()}
              </div>
            </div>
            <button
              onClick={nextMonth}
              aria-label="Next month"
              className="w-12 h-12 flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 transition-colors font-mono text-2xl leading-none justify-self-end"
              onMouseEnter={e =>
                (e.currentTarget.style.backgroundColor =
                  theme.surface3)
              }
              onMouseLeave={e =>
                (e.currentTarget.style.backgroundColor = '')
              }
            >
              ›
            </button>
          </div>
        </div>

        {/* Events loading/offline/error indicator */}
        <EventsStatusIndicator />

        {/* Day-of-week headers */}
        <div className="grid grid-cols-7 mb-1">
          {DAYS.map(d => (
            <div
              key={d}
              className="text-center font-mono text-xs text-ink-400 uppercase tracking-widest py-1"
            >
              {d}
            </div>
          ))}
        </div>

        <CalendarGrid />
      </div>

      {/* Right: Event panel (desktop only) */}
      {isDesktop && panelOpen && (
        <EventPanel
          mode={editEventId ? 'edit' : 'create'}
          calendarId={calendarId}
          defaultDate={createForDate ?? undefined}
          eventId={editEventId ?? undefined}
          occurrenceDate={editOccurrenceDate ?? undefined}
          onClose={closePanel}
        />
      )}

      {/* Day Events Modal (desktop overlay) */}
      {isDesktop && expandedDay && (
        <DayEventsModal
          date={expandedDay}
          events={(() => {
            const dayEvents = eventsOnDay(
              expandedDay.getDate()
            );
            const dayDetailsEvents = [
              ...dayEvents,
              ...multiDayOnDay(
                expandedDay.getDate()
              ).filter(
                e => !dayEvents.some(o => o.id === e.id)
              ),
            ];
            console.log('[DesktopDayDetails]', {
              date: expandedDay,
              expandedEventsCount: expandedEvents.length,
              allDayCount: dayDetailsEvents.filter(
                e => e.all_day
              ).length,
              timedCount: dayDetailsEvents.filter(
                e => !e.all_day
              ).length,
              events: dayDetailsEvents.map(e => ({
                id: e.id,
                title: e.title,
                all_day: e.all_day,
                start_date: e.start_date,
                end_date: e.end_date,
                start_at: e.start_at,
              })),
            });
            return dayDetailsEvents;
          })()}
          calendarColorMap={calendarColorMap}
          currentCalendarColor={calColor}
          holidays={
            holidaysByDay.get(expandedDay.getDate()) ?? []
          }
          onClose={closeDayModal}
          onEventClick={handleModalEventClick}
          onCreateEvent={handleModalCreateEvent}
        />
      )}

      {/* Guest subscribe modal — auto-shown for temporary visitors */}
      {showGuestModal && currentCalendar && (
        <GuestSubscribeModal
          calendarName={currentCalendar.name}
          calColor={calColor}
          isPending={
            createSubscription.isPending ||
            createSubscription.isSuccess
          }
          onSubscribe={() => {
            const token =
              joinToken ?? currentCalendar?.join_token;
            if (
              token &&
              meData?.user?.personal_calendar_id
            ) {
              createSubscription.mutate({
                join_token: token,
                subscriber_calendar_id:
                  meData.user.personal_calendar_id,
              });
            }
          }}
          onDismiss={() => setGuestModalDismissed(true)}
        />
      )}
    </div>
  );
}
